import { inlineImagesForPrint } from './imageUtils';
import { getBillTemplateRoot } from './billPdfExport';

const QUOTATION_PRINT_CSS = `
@media print {
  #quotation-template {
    min-height: auto !important;
    height: auto !important;
    display: block !important;
    width: 210mm !important;
    max-width: 210mm !important;
    overflow: visible !important;
  }
  #quotation-template .quotation-post-items { margin-top: 4mm !important; }
  #quotation-template .quotation-print-inner,
  #quotation-template .quotation-table-wrap {
    transform: none !important;
    width: 100% !important;
    min-height: auto !important;
  }
}
`;

const INVOICE_TEMPLATE_SELECTOR =
  '#invoice-template, #quotation-template, #challan-template, #modern-sales-template, #simple-sales-template, #invoice-a5-template';

export const BILL_PRINT_WINDOW_CHROME_CSS = `
html, body {
  margin: 0;
  padding: 0;
  background: #fff;
  box-sizing: border-box;
}
@media print {
  html, body {
    width: 210mm !important;
    max-width: 210mm !important;
    height: auto !important;
    overflow: visible !important;
  }
}
@page {
  size: A4 portrait;
  margin: 8mm;
}
.bill-print-root {
  width: 210mm;
  max-width: 210mm;
  margin: 0 auto;
  position: relative;
  overflow: visible !important;
  box-sizing: border-box;
}
.print\\:hidden { display: none !important; }
@media print { .print\\:hidden { display: none !important; } }
.print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
.print\\:bg-white { background-color: white !important; }
.print\\:border { border-width: 1px !important; }
.print\\:border-gray-300 { border-color: #d1d5db !important; }
.print\\:flex-row { flex-direction: row !important; }
.print\\:bg-yellow-100 { background-color: #fef3c7 !important; }
.print\\:bg-green-100 { background-color: #dcfce7 !important; }
.print\\:transform-none { transform: none !important; }
@media print {
  .grid { display: grid !important; }
  .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .flex { display: flex !important; }
  .flex-row { flex-direction: row !important; }
  [class*="print:hidden"] { display: none !important; }
  button { display: none !important; }
  .sticky { position: static !important; }
  img {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  ${INVOICE_TEMPLATE_SELECTOR} {
    width: 210mm !important;
    max-width: 210mm !important;
    min-height: auto !important;
    height: auto !important;
    margin: 0 auto !important;
    padding: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    overflow: visible !important;
    transform: none !important;
    page-break-inside: avoid;
  }
  #invoice-template .invoice-print-inner,
  #quotation-template .quotation-print-inner,
  #challan-template .invoice-print-inner {
    transform: none !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
  }
  tr.invoice-filler-row { display: none !important; }
}
${QUOTATION_PRINT_CSS}
`;

async function waitDocumentImages(doc) {
  const imgs = Array.from(doc?.images || []);
  if (!imgs.length) return;
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        })
    )
  );
}

function normalizeTemplateForPrint(root) {
  if (!root) return;

  root.querySelectorAll(INVOICE_TEMPLATE_SELECTOR).forEach((el) => {
    el.classList.remove('invoice-screen-preview-boost');
    el.classList.remove('quotation-screen-preview-boost');
    el.classList.remove('invoice-pdf-export');
    el.classList.remove('invoice-image-export');
    el.style.transform = 'none';
    el.style.width = '210mm';
    el.style.maxWidth = '210mm';
    el.style.minHeight = 'auto';
    el.style.height = 'auto';
    el.style.margin = '0 auto';
    el.style.boxShadow = 'none';
    el.style.borderRadius = '0';
    el.style.overflow = 'visible';
  });

  root.querySelectorAll('.invoice-print-inner, .quotation-print-inner').forEach((el) => {
    el.style.transform = 'none';
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    el.style.marginLeft = '0';
    el.style.marginRight = '0';
  });

  root.querySelectorAll('tr.invoice-filler-row').forEach((tr) => {
    tr.style.display = 'none';
  });

  root.querySelectorAll('button').forEach((btn) => {
    btn.style.display = 'none';
  });
}

/** Clone bill preview DOM and strip screen-only scaling before print/PDF prep. */
export async function prepareBillPrintBodyHtml(container) {
  if (!container) {
    throw new Error('Print container not found');
  }

  const temp = document.createElement('div');
  temp.className = 'bill-print-root';
  temp.innerHTML = container.innerHTML;

  normalizeTemplateForPrint(temp);
  await inlineImagesForPrint(temp);

  return temp.outerHTML;
}

/** Open a dedicated print window (same approach as Sales invoice print). */
export async function printBillFromContainer(container, { title = 'Bill' } = {}) {
  const bodyHtml = await prepareBillPrintBodyHtml(container);
  const printWindow = window.open('', '_blank', 'height=900,width=1000');
  if (!printWindow) {
    throw new Error('Please allow popups to print.');
  }

  printWindow.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
      '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">' +
      `<style>${BILL_PRINT_WINDOW_CHROME_CSS}</style></head>` +
      '<body style="margin:0;background:#fff;">' +
      bodyHtml +
      '</body></html>'
  );
  printWindow.document.close();
  printWindow.focus();

  await new Promise((resolve) => {
    if (printWindow.document.readyState === 'complete') resolve();
    else printWindow.addEventListener('load', resolve, { once: true });
  });

  await waitDocumentImages(printWindow.document);
  await new Promise((r) => setTimeout(r, 300));

  printWindow.print();
  printWindow.close();
}
